import React from 'react'
import { useFormContext } from 'react-hook-form'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/common/components/ui/form.tsx'
import { ClassValue } from 'clsx'
import { cn } from '@/utils/style.ts'
import { last, startCase } from 'lodash-es'
import { useFormSection } from '@/common/components/Form/useFormSection.tsx'

type BaseFormFieldWrapperProps = {
  name: string
  label?: string
  className?: ClassValue
  children: React.ReactNode
  disabled?: boolean
}

type FormFieldWrapperProps = {
  name: string
  label?: string
  description?: string
  className?: ClassValue
  disabled?: boolean
}

export type WrappedInputProps = {
  className?: ClassValue
  wrapperClassName?: ClassValue
} & FormFieldWrapperProps

export const nameToLabel = (name: string) => startCase(last(name.split('.')))

const BaseFormFieldWrapper: React.FC<BaseFormFieldWrapperProps> = ({ name, label, className, disabled, children }) => {
  const { control } = useFormContext()
  const { fieldName } = useFormSection(name)
  return (
    <FormField
      control={control}
      name={fieldName}
      render={() => (
        <FormItem className={cn('form-field-wrapper flex flex-col relative', className)}>
          <FormLabel
            className={cn('mb-2', {
              'opacity-50': disabled,
            })}
          >{label ?? nameToLabel(fieldName)}</FormLabel>
          {children}
        </FormItem>
      )}
    />
  )
}

export const FormFieldWrapper: React.FC<FormFieldWrapperProps & {
  children: React.ReactNode
}> = ({ name, label, description, className, disabled = false, children }) => {
  return (
    <BaseFormFieldWrapper name={name} label={label} className={className} disabled={disabled}>
      <FormControl>
        {children}
      </FormControl>
      {description ? <FormDescription>{description}</FormDescription> : null}
      <FormMessage />
    </BaseFormFieldWrapper>
  )
}

export const PopoverFormFieldWrapper: React.FC<FormFieldWrapperProps & {
  children: React.ReactNode
}> = ({ name, label, description, className, disabled = false, children }) => {
  return (
    <BaseFormFieldWrapper name={name} label={label} className={className} disabled={disabled}>
      {/*Handle popover in children*/}
      {children}
      {description ? <FormDescription>{description}</FormDescription> : null}
      <FormMessage />
    </BaseFormFieldWrapper>
  )
}

export const SwitchFieldWrapper: React.FC<FormFieldWrapperProps & {
  children: React.ReactNode
}> = ({ name, label, description, className, disabled = false, children }) => {
  return (
    <BaseFormFieldWrapper name={name} label={label} className={className} disabled={disabled}>
      <div className="flex flex-row items-center justify-between rounded-md px-3 py-1 space-x-2 border bg-background">
        {children}
        {description ? <FormDescription>{description}</FormDescription> : null}
      </div>
      <FormMessage />
    </BaseFormFieldWrapper>
  )
}